import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { farmvestService } from '../../../services/farmvest_api';
import { FarmvestTicket, TicketStats, UpdateTreatmentDetailsRequest } from '../../../types/farmvest';
import { setSnackbar, addTicketToast } from '../uiSlice';

export const fetchTickets = createAsyncThunk(
    'farmvestTickets/fetchTickets',
    async (params: {
        ticket_type?: string;
        status_filter?: string;
        transfer_direction?: string;
        farm_id?: number;
        shed_id?: number;
        page?: number;
        size?: number;
        search?: string;
        start_date?: string;
        end_date?: string;
    } | undefined, { rejectWithValue }) => {
        try {
            const response = await farmvestService.getTickets(params);

            let tickets: FarmvestTicket[] = [];
            let counts: any = null;
            let pagination: any = null;

            // Robust ticket list extraction
            const findTickets = (obj: any): FarmvestTicket[] => {
                if (Array.isArray(obj)) return obj;
                if (!obj || typeof obj !== 'object') return [];
                const priorityKeys = ['tickets', 'health_tickets', 'healthTickets', 'items', 'list', 'data', 'results', 'payload', 'records'];
                for (const key of priorityKeys) {
                    if (Array.isArray(obj[key])) return obj[key];
                }
                // Recursive search for first array
                for (const key in obj) {
                    if (Array.isArray(obj[key])) return obj[key];
                    if (obj[key] && typeof obj[key] === 'object') {
                        const nested = findTickets(obj[key]);
                        if (nested.length > 0) return nested;
                    }
                }
                return [];
            };

            tickets = findTickets(response);

            // Robust count extraction - prioritize all_tickets if available
            counts = response.all_tickets || response.counts || response.data?.all_tickets || response.data?.counts;

            // Fallback: Check for root-level status counts if counts object is missing or incomplete
            if (response) {
                if (!counts) counts = {};

                // Map root-level keys if they exist and counts doesn't have them
                const mapping = {
                    total: ['total', 'total_items', 'total_count', 'count', 'total_tickets'],
                    pending: ['pending', 'pending_count', 'pending_tickets'],
                    in_progress: ['in_progress', 'in_progress_count', 'in_progress_tickets', 'inprogress', 'progress'],
                    under_treatment: ['under_treatment', 'under_treatment_count', 'under_treatment_tickets', 'underTreatment'],
                    treatment_finished: ['treatment_finished', 'treatment_finished_count', 'treatment_finished_tickets', 'treatmentFinished'],
                    quarantine_pending: ['quarantine_pending', 'quarantine_pending_count', 'quarantine_pending_tickets', 'quarantinePending'],
                    approved: ['approved', 'approved_count', 'approved_tickets'],
                    completed: ['completed', 'completed_count', 'completed_tickets', 'resolved', 'resolved_count'],
                    rejected: ['rejected', 'rejected_count', 'rejected_tickets']
                };

                Object.entries(mapping).forEach(([target, sources]) => {
                    if (counts[target] === undefined || counts[target] === 0) {
                        for (const source of sources) {
                            if (response[source] !== undefined) {
                                counts[target] = Number(response[source]);
                                break;
                            }
                            // Deep check in common stat objects
                            if (response.all_tickets?.[source] !== undefined) {
                                counts[target] = Number(response.all_tickets[source]);
                                break;
                            }
                            if (response.counts?.all_tickets?.[source] !== undefined) {
                                counts[target] = Number(response.counts.all_tickets[source]);
                                break;
                            }
                            if (response.data?.all_tickets?.[source] !== undefined) {
                                counts[target] = Number(response.data.all_tickets[source]);
                                break;
                            }
                            if (response.data?.[source] !== undefined) {
                                counts[target] = Number(response.data[source]);
                                break;
                            }
                        }
                    }
                });

                // Clean up if we didn't find anything
                if (Object.keys(counts).length === 0) counts = null;
            }

            pagination = response.pagination || response.data?.pagination;

            // Normalize pagination
            if (pagination || (counts && counts.total)) {
                if (!pagination) pagination = {};
                const totalItems = Number(pagination.total_items || pagination.totalItems || pagination.total_count || pagination.count || counts?.total || tickets.length || 0);
                let totalPages = Number(pagination.total_pages || pagination.totalPages || pagination.total_page || pagination.totalPage || 0);
                const currentPage = Number(pagination.current_page || pagination.currentPage || pagination.page || params?.page || 1);
                const itemsPerPage = Number(pagination.items_per_page || pagination.itemsPerPage || pagination.size || params?.size || 15);

                if (totalPages === 0 && totalItems > 0) {
                    totalPages = Math.ceil(totalItems / itemsPerPage);
                }

                pagination = {
                    total_items: totalItems,
                    total_pages: totalPages,
                    current_page: currentPage,
                    items_per_page: itemsPerPage
                };
            }

            return {
                tickets,
                counts,
                pagination,
            };
        } catch (error: any) {
            const errorData = error.response?.data?.detail || error.response?.data || error.message || 'Failed to fetch tickets';
            return rejectWithValue(typeof errorData === 'object' ? JSON.stringify(errorData) : errorData);
        }
    }
);


export const createTicket = createAsyncThunk(
    'farmvestTickets/createTicket',
    async ({ ticketType, payload }: { ticketType: string; payload: any }, { rejectWithValue, dispatch }) => {
        const ticketLabel = ticketType.replace(/_/g, ' ').toLowerCase()
            .replace(/\b\w/g, (c: string) => c.toUpperCase());

        // ── Fire UI feedback IMMEDIATELY (don't wait for API) ──
        dispatch(setSnackbar({
            message: `🎫 ${ticketLabel} ticket raised successfully!`,
            type: 'success'
        }));

        // Web notification (if permission granted)
        if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
            new Notification(`🎫 ${ticketLabel} Ticket Raised`, {
                body: `Your ${ticketLabel} ticket has been submitted.`,
                icon: '/logo192.png',
                tag: `ticket-create-${Date.now()}`,
            });
        }

        try {
            const response = await farmvestService.createTicket(ticketType, payload);

            dispatch(fetchTickets(undefined));
            return response;
        } catch (error: any) {
            console.error('[createTicket] API error:', error);
            const errorData = error.response?.data?.detail || error.response?.data || error.message || 'Failed to create ticket';
            return rejectWithValue(typeof errorData === 'object' ? JSON.stringify(errorData) : errorData);
        }
    }
);


export const updateTreatment = createAsyncThunk(
    'farmvestTickets/updateTreatment',
    async (payload: UpdateTreatmentDetailsRequest, { rejectWithValue, dispatch }) => {
        try {
            const response = await farmvestService.updateTreatmentDetails(payload);
            dispatch(fetchTickets(undefined));
            return response;
        } catch (error: any) {
            const errorData = error.response?.data?.detail || error.response?.data || error.message || 'Failed to update treatment details';
            return rejectWithValue(typeof errorData === 'object' ? JSON.stringify(errorData) : errorData);
        }
    }
);

export const assignTicket = createAsyncThunk(
    'farmvestTickets/assignTicket',
    async ({ id, assistantId, doctorId }: { id: number; assistantId?: number; doctorId?: number }, { rejectWithValue, dispatch }) => {
        try {
            const response = await farmvestService.assignTicket(id, { assistant_id: assistantId, doctor_id: doctorId });
            dispatch(fetchTickets(undefined));
            return response;
        } catch (error: any) {
            const errorData = error.response?.data?.detail || error.response?.data || error.message || 'Failed to assign ticket';
            return rejectWithValue(typeof errorData === 'object' ? JSON.stringify(errorData) : errorData);
        }
    }
);

interface TicketsState {
    tickets: FarmvestTicket[];
    counts: TicketStats | null;
    stats: TicketStats | null; // New persistent stats
    loading: boolean;
    createLoading: boolean;
    error: string | null;
    successMessage: string | null;
    pagination: { current_page: number; total_pages: number; total_items: number; items_per_page?: number } | null;
}

const initialState: TicketsState = {
    tickets: [],
    counts: null,
    stats: null,
    loading: false,
    createLoading: false,
    error: null,
    successMessage: null,
    pagination: null,
};

const ticketsSlice = createSlice({
    name: 'farmvestTickets',
    initialState,
    reducers: {
        clearTicketMessages: (state) => {
            state.error = null;
            state.successMessage = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTickets.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchTickets.fulfilled, (state, action) => {
                state.loading = false;
                state.tickets = Array.from(
                    new Map(action.payload.tickets.map((t: FarmvestTicket) => [t.id, t])).values()
                );
                state.counts = action.payload.counts;
                state.pagination = action.payload.pagination;
                if (action.payload.counts) {
                    state.stats = action.payload.counts;
                }
            })
            .addCase(fetchTickets.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(createTicket.pending, (state) => {
                state.createLoading = true;
                state.error = null;
                state.successMessage = null;
            })
            .addCase(createTicket.fulfilled, (state, action) => {
                state.createLoading = false;
                state.successMessage = action.payload?.message || 'Ticket created successfully';
            })
            .addCase(createTicket.rejected, (state, action) => {
                state.createLoading = false;
                state.error = typeof action.payload === 'string' ? action.payload : JSON.stringify(action.payload);
            })
            .addCase(updateTreatment.pending, (state) => {
                state.error = null;
                state.successMessage = null;
            })
            .addCase(updateTreatment.fulfilled, (state, action) => {
                state.successMessage = action.payload?.message || 'Treatment details updated successfully';
            })
            .addCase(updateTreatment.rejected, (state, action) => {
                state.error = action.payload as string;
            })
            .addCase(assignTicket.pending, (state) => {
                state.error = null;
            })
            .addCase(assignTicket.fulfilled, (state, action) => {
                state.successMessage = action.payload?.message || 'Ticket assigned successfully';
            })
            .addCase(assignTicket.rejected, (state, action) => {
                state.error = action.payload as string;
            });
    },
});

export const { clearTicketMessages } = ticketsSlice.actions;
export const ticketsReducer = ticketsSlice.reducer;
export default ticketsSlice.reducer;
